/**
 * Plays the hover video rendered by snippets/product-card-hover-media.liquid.
 *
 * The visibility of the overlay is pure CSS -- `.card-gallery:hover` fades it
 * in, and a card whose metafield holds an image needs nothing else. This script
 * exists only for the video case, where two things cannot be done in CSS:
 *
 *   1. Deferring the download. The <source> elements ship with `data-src`, so
 *      the browser has nothing to fetch until the first hover promotes them.
 *      Without this, every card in a tab would pull a video on page load.
 *   2. Starting and stopping playback.
 *
 * Deliberately a bare custom element rather than a Component subclass: it owns
 * no refs and no state beyond "has this loaded yet", and its listeners belong on
 * the .card-gallery ancestor -- the element the user actually hovers -- rather
 * than on itself, which is pointer-events: none.
 */
class ProductHoverMedia extends HTMLElement {
  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {Element | null} */
  #trigger = null;

  #loaded = false;

  connectedCallback() {
    this.#video = this.querySelector('video');
    if (!this.#video) return;

    // The gallery is what the pointer is over; this element is a transparent
    // overlay on top of it. Falls back to the parent so the behaviour survives
    // the snippet being rendered outside a card gallery.
    this.#trigger = this.closest('.card-gallery') ?? this.parentElement;
    if (!this.#trigger) return;

    // Safari has been known to reject play() on a video that is only muted by
    // attribute, so mirror it onto the property.
    this.#video.muted = true;

    this.#trigger.addEventListener('pointerenter', this.#handleEnter);
    this.#trigger.addEventListener('pointerleave', this.#handleLeave);
    this.#trigger.addEventListener('focusin', this.#handleEnter);
    this.#trigger.addEventListener('focusout', this.#handleLeave);
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('pointerenter', this.#handleEnter);
    this.#trigger?.removeEventListener('pointerleave', this.#handleLeave);
    this.#trigger?.removeEventListener('focusin', this.#handleEnter);
    this.#trigger?.removeEventListener('focusout', this.#handleLeave);
    this.#trigger = null;
  }

  /**
   * Matches the CSS that reveals the overlay. A tap on a touch screen fires
   * pointerenter too, and there is no matching pointerleave, so a video started
   * there would keep playing behind an overlay that CSS never faded in.
   *
   * @param {Event} event
   */
  #handleEnter = (event) => {
    if (event instanceof PointerEvent && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    // Under reduced motion the overlay still fades in, but on the poster frame:
    // the video is never fetched and never plays.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const video = this.#video;
    if (!video) return;

    if (!this.#loaded) {
      this.#loaded = true;

      for (const source of video.querySelectorAll('source[data-src]')) {
        const url = source.getAttribute('data-src');
        if (url) source.setAttribute('src', url);
      }

      video.load();
    }

    // Rejects when the pointer leaves before the video is ready to play, which
    // is not an error worth surfacing.
    video.play().catch(() => {});
  };

  #handleLeave = () => {
    const video = this.#video;
    if (!video) return;

    video.pause();
    // Back to the first frame, so the next hover starts the clip over rather
    // than resuming wherever the pointer happened to leave.
    video.currentTime = 0;
  };
}

if (!customElements.get('product-hover-media')) {
  customElements.define('product-hover-media', ProductHoverMedia);
}
